import { createElement } from 'react';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@/lib/trpc/router';
import { UnifiedAdditionalRecordClassificationType } from '@/feature/patients/types';
import { PathologyTemplate } from '../documents/templates';
import { ProcedureNoteContent } from '../documents/templates/ProcedureNoteContent';
import {
  adaptAdditionalRecords,
  type AdditionalRecordsKey,
} from '../documents/viewer/adapters/additionalRecords';
import { adaptAllergiesData } from '../documents/viewer/adapters/allergiesData';
import { adaptEncountersRecords } from '../documents/viewer/adapters/encountersRecords';
import { adaptImagingRecords } from '../documents/viewer/adapters/imagingRecords';
import { adaptLabResultsData } from '../documents/viewer/adapters/labResultsData';
import { adaptMedicationsData } from '../documents/viewer/adapters/medicationsData';
import { adaptPatientProviders } from '../documents/viewer/adapters/patientProviders';
import { adaptProblemsData } from '../documents/viewer/adapters/problemsData';
import { adaptProceduresData } from '../documents/viewer/adapters/proceduresData';
import { adaptProviderNotesRecords } from '../documents/viewer/adapters/providerNotesRecords';
import { adaptVaccinationsData } from '../documents/viewer/adapters/vaccinationsData';
import { adaptVitalsData } from '../documents/viewer/adapters/vitalsData';
import {
  DocumentSource,
  DocumentType,
  type DocumentEntry,
} from '../documents/viewer/types';
import { ReferenceUnresolvedContent } from './ReferenceUnresolvedContent';
import { mapFacesheetSourceType } from './facesheetSourceType';
import type { FacesheetSourceContent } from './types';

type CuratedRecord = NonNullable<
  inferRouterOutputs<AppRouter>['patientsRouter']['getPatientFacesheetReferenceDetail']['curatedRecord']
>;

type StringItems = string[] | { items?: string[] } | null | undefined;
const NO_HIGHLIGHT_ID = '__facesheet-no-highlight__';

const unwrapItems = (value?: StringItems): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return value.items ?? [];
};

const compactJoin = (values: Array<string | undefined | null>) =>
  values.filter(Boolean).join(' ') || undefined;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const entryToSourceContent = (
  entry: DocumentEntry | undefined,
  fallbackMessage: string,
): FacesheetSourceContent => {
  if (!entry) {
    return {
      kind: 'template',
      render: () =>
        createElement(ReferenceUnresolvedContent, {
          reason: 'missingContent',
          message: fallbackMessage,
        }),
    };
  }

  if (entry.kind === 'pdf') {
    if (!entry.signedUrl) {
      return {
        kind: 'template',
        render: () =>
          createElement(ReferenceUnresolvedContent, {
            reason: 'missingContent',
            message: fallbackMessage,
          }),
      };
    }
    return { kind: 'pdf', signedUrl: entry.signedUrl };
  }

  return {
    kind: 'template',
    anchorId: entry.highlightAnchorId,
    render: (ctx) => entry.render(ctx),
  };
};

const buildDemographicSource = (
  record: Extract<CuratedRecord, { recordType: 'demographic' }>['record'],
): FacesheetSourceContent => {
  const phone = unwrapItems(record.telephones)[0];
  const name = compactJoin([
    record.givenName,
    record.middleName,
    record.familyName,
  ]);
  const address = compactJoin([
    record.addressLine1,
    record.addressLine2,
    record.city,
    record.state,
    record.postalCode,
  ]);

  return {
    kind: 'template',
    render: () =>
      createElement(
        'div',
        {
          className: 'mx-auto max-w-3xl rounded-lg bg-background p-6 shadow-sm',
          role: 'document',
          'aria-label': 'Demographics reference',
        },
        createElement(
          'h2',
          { className: 'mb-4 text-lg font-semibold text-foreground' },
          'Patient Demographics',
        ),
        createElement(
          'dl',
          { className: 'grid gap-3 text-sm text-foreground' },
          ...[
            ['Name', name],
            ['Date of Birth', record.dateOfBirth],
            ['Gender', record.gender],
            ['Phone', phone],
            ['Address', address],
          ].map(([label, value]) =>
            createElement(
              'div',
              { key: label, className: 'grid grid-cols-[8rem_1fr] gap-2' },
              createElement(
                'dt',
                { className: 'font-medium text-muted-foreground' },
                label,
              ),
              createElement('dd', null, value || 'Unknown'),
            ),
          ),
        ),
      ),
  };
};

const wrapAdditionalRecords = ({
  records,
  title,
  classificationType,
  curatedField,
  highlightRecordId,
}: {
  records: Array<{
    recordId: string;
    sourceType: string | null;
    curatedValue: Record<string, unknown>;
  }>;
  title?: string;
  classificationType: UnifiedAdditionalRecordClassificationType;
  curatedField:
    | 'curatedUnifiedAdditionalAllergy'
    | 'curatedUnifiedAdditionalFamilyHistory'
    | 'curatedUnifiedAdditionalSocialHistory'
    | 'curatedUnifiedAdditionalVaccination'
    | 'curatedUnifiedAdditionalReferenceDocument'
    | 'curatedUnifiedAdditionalProvider';
  highlightRecordId?: string;
}): DocumentEntry | undefined => {
  const firstRecord = records[0];
  if (!firstRecord) return undefined;

  const createdAt = new Date().toISOString();
  return adaptAdditionalRecords(
    {
      details: records.map(({ recordId, sourceType, curatedValue }) => ({
        id: recordId,
        name: title ?? recordId,
        sourceType: sourceType ?? 'unspecified',
        createdAt,
        sources: [
          {
            contentType: 'curated',
            createdAt,
            [curatedField]: curatedValue,
          },
        ],
      })),
    },
    {
      source: DocumentSource.MEDICAL_RECORDS,
      type: DocumentType.ADDITIONAL,
      additionalRecordId: firstRecord.recordId,
      classificationType,
      sourceType: mapFacesheetSourceType(firstRecord.sourceType),
      name: title,
      highlightRecordId,
    } satisfies AdditionalRecordsKey,
  );
};

/**
 * Thin wrap: package a facesheet curatedRecord into the list/detail shape, ( also solve some fields name mismatch )
 * expected by existing document adapters, then reuse adapter → template.
 */
export const mapCuratedRecordToSourceContent = (
  curated: CuratedRecord,
  title?: string,
  options?: { highlightRecordId?: string },
): FacesheetSourceContent => {
  const { sourceType, recordType, record } = curated;
  const source = mapFacesheetSourceType(sourceType);
  const recordId =
    typeof (record as { id?: unknown }).id === 'string'
      ? (record as { id: string }).id
      : 'reference';
  const highlightId =
    options?.highlightRecordId === recordId ? recordId : NO_HIGHLIGHT_ID;

  switch (recordType) {
    case 'allergy':
      return entryToSourceContent(
        adaptAllergiesData(
          { allergies: [record as never] },
          {
            source: DocumentSource.MEDICAL_DATA,
            type: DocumentType.ALLERGIES,
            allergyId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render allergy reference.',
      );

    case 'problem':
      return entryToSourceContent(
        adaptProblemsData(
          { problems: [record as never] },
          {
            source: DocumentSource.MEDICAL_DATA,
            type: DocumentType.PROBLEMS,
            problemId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render problem reference.',
      );

    case 'procedure':
      return entryToSourceContent(
        adaptProceduresData(
          { procedures: [record as never] },
          {
            source: DocumentSource.MEDICAL_DATA,
            type: DocumentType.PROCEDURES,
            procedureId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render procedure reference.',
      );

    case 'medication': {
      const medicationRecord = asRecord(record);
      const normalized = {
        ...medicationRecord,
        id: recordId,
        medicationCodes: unwrapItems(
          medicationRecord.medicationCodes as StringItems,
        ),
        surescriptsMedicationDetails:
          medicationRecord.surescriptsMedicationDetails ??
          medicationRecord.surescriptsDetails,
      };
      return entryToSourceContent(
        adaptMedicationsData(
          { medications: [normalized as never] },
          {
            source: DocumentSource.MEDICAL_DATA,
            type: DocumentType.MEDICATIONS,
            medicationId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render medication reference.',
      );
    }

    case 'vaccination':
      return entryToSourceContent(
        adaptVaccinationsData(
          {
            vaccinations: [
              {
                ...(record as object),
                id: recordId,
                name:
                  typeof (record as { name?: unknown }).name === 'string'
                    ? (record as { name: string }).name
                    : 'Vaccination',
              } as never,
            ],
          },
          {
            source: DocumentSource.MEDICAL_DATA,
            type: DocumentType.VACCINATIONS,
            vaccinationId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render vaccination reference.',
      );

    case 'vital': {
      const vital = asRecord(record);
      const normalized = {
        ...vital,
        id: recordId,
        name:
          (vital.observationName as string | undefined) ??
          (vital.name as string | undefined),
        value:
          (vital.observationValue as string | undefined) ??
          (vital.value as string | undefined),
        unit:
          (vital.observationUnit as string | undefined) ??
          (vital.unit as string | undefined),
        observedAt:
          (vital.observationDatetime as string | undefined) ??
          (vital.observedAt as string | undefined),
        text:
          (vital.observationText as string | undefined) ??
          (vital.text as string | undefined),
        category:
          (vital.observationCategory as string | undefined) ??
          (vital.category as string | undefined),
      };
      return entryToSourceContent(
        adaptVitalsData(
          {
            groupingObservationId:
              (vital.groupingObservationId as string | undefined) ?? recordId,
            vitals: [normalized as never],
          },
          {
            source: DocumentSource.MEDICAL_DATA,
            type: DocumentType.VITALS,
            vitalId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render vital reference.',
      );
    }

    case 'labPanel': {
      const panel = asRecord(record);
      return entryToSourceContent(
        adaptLabResultsData(
          {
            labPanels: [
              {
                ...panel,
                id: recordId,
                rows: (panel.rows as unknown[] | undefined) ?? [],
                interpreters:
                  (panel.interpreters as unknown[] | undefined) ?? [],
                performers: (panel.performers as unknown[] | undefined) ?? [],
              } as never,
            ],
          },
          {
            source: DocumentSource.MEDICAL_DATA,
            type: DocumentType.LAB_RESULTS,
            labPanelId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render lab panel reference.',
      );
    }

    case 'labResult': {
      const lab = asRecord(record);
      return entryToSourceContent(
        adaptLabResultsData(
          {
            labPanels: [
              {
                id: recordId,
                labPanel:
                  (lab.labTest as string | undefined) ??
                  (lab.name as string | undefined) ??
                  'Lab Result',
                dateOfStudy:
                  (lab.dateOfStudy as string | undefined) ??
                  (lab.recordedDatetime as string | undefined),
                rows: [
                  {
                    labTest:
                      (lab.labTest as string | undefined) ??
                      (lab.name as string | undefined),
                    value: lab.value as string | undefined,
                    units:
                      (lab.units as string | undefined) ??
                      (lab.unit as string | undefined),
                    dateOfStudy:
                      (lab.dateOfStudy as string | undefined) ??
                      (lab.recordedDatetime as string | undefined),
                    interpretation: lab.interpretation as string | undefined,
                  },
                ],
                interpreters: [],
                performers: [],
              },
            ],
          },
          {
            source: DocumentSource.MEDICAL_DATA,
            type: DocumentType.LAB_RESULTS,
            labPanelId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render lab result reference.',
      );
    }

    case 'encounter': {
      const encounter = asRecord(record);
      const documentReferences = (
        (encounter.documentReferences as Array<Record<string, unknown>>) ?? []
      ).map((docRef) => {
        const metadata = docRef.documentMetadata as
          | { signedUrl?: string }
          | undefined;
        return {
          referenceType: docRef.referenceType as string | undefined,
          contentType: docRef.contentType as string | undefined,
          signedUrl:
            (docRef.signedUrl as string | undefined) ?? metadata?.signedUrl,
        };
      });

      return entryToSourceContent(
        adaptEncountersRecords(
          {
            encounters: [
              {
                id: recordId,
                name:
                  (encounter.typeName as string | undefined) ??
                  (encounter.encounterText as string | undefined) ??
                  'Encounter',
                dateOfStudy:
                  (encounter.startTime as string | undefined) ??
                  (encounter.endTime as string | undefined) ??
                  '',
                sourceType: sourceType ?? 'unspecified',
                curated: {
                  id: recordId,
                  encounterText: encounter.encounterText as string | undefined,
                  typeName: encounter.typeName as string | undefined,
                  startTime: encounter.startTime as string | undefined,
                  endTime: encounter.endTime as string | undefined,
                  hospitalizationDischargeDisposition:
                    encounter.hospitalizationDischargeDisposition as
                      | string
                      | undefined,
                  documentReferences,
                  locations: (encounter.locations as never) ?? [],
                  conditions: (encounter.conditions as never) ?? [],
                  practitioners: (encounter.practitioners as never) ?? [],
                },
              },
            ],
          },
          {
            source: DocumentSource.MEDICAL_RECORDS,
            type: DocumentType.ENCOUNTERS,
            encounterId: highlightId,
            highlightEncounterId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render encounter reference.',
      );
    }

    case 'familyHistory':
      return entryToSourceContent(
        wrapAdditionalRecords({
          records: [{ recordId, sourceType, curatedValue: asRecord(record) }],
          title,
          classificationType:
            UnifiedAdditionalRecordClassificationType.FAMILY_HISTORY,
          curatedField: 'curatedUnifiedAdditionalFamilyHistory',
          highlightRecordId: highlightId,
        }),
        'Unable to render family history reference.',
      );

    case 'socialHistory':
      return entryToSourceContent(
        wrapAdditionalRecords({
          records: [{ recordId, sourceType, curatedValue: asRecord(record) }],
          title,
          classificationType:
            UnifiedAdditionalRecordClassificationType.SOCIAL_HISTORY,
          curatedField: 'curatedUnifiedAdditionalSocialHistory',
          highlightRecordId: highlightId,
        }),
        'Unable to render social history reference.',
      );

    case 'additionalReferenceDocument':
      return entryToSourceContent(
        wrapAdditionalRecords({
          records: [{ recordId, sourceType, curatedValue: asRecord(record) }],
          title,
          classificationType:
            UnifiedAdditionalRecordClassificationType.ADDITIONAL_REFERENCE_DOCUMENTS,
          curatedField: 'curatedUnifiedAdditionalReferenceDocument',
          highlightRecordId: highlightId,
        }),
        'Unable to render reference document.',
      );

    case 'provider':
      return entryToSourceContent(
        adaptPatientProviders(
          {
            providers: [
              {
                ...(record as object),
                id: recordId,
                telephones: unwrapItems(
                  (record as { telephones?: StringItems }).telephones,
                ),
              } as never,
            ],
          },
          {
            source: DocumentSource.PROVIDERS,
            type: DocumentType.PATIENT_PROVIDERS,
            providerId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render provider reference.',
      );

    case 'imagingNote': {
      const imaging = asRecord(record);
      const imagingType = imaging.imagingType as string | undefined;
      const bodyPart = imaging.bodyPart as string | undefined;
      return entryToSourceContent(
        adaptImagingRecords(
          {
            details: [
              {
                id: recordId,
                curated: {
                  id: recordId,
                  title:
                    (imaging.imagingNoteName as string | undefined) ??
                    'Imaging',
                  date: (imaging.date as string | undefined) ?? '',
                  comments: imaging.notes as string | undefined,
                  createdAt:
                    (imaging.dateUploaded as string | undefined) ??
                    new Date().toISOString(),
                  imagingType: imagingType
                    ? { name: imagingType, displayName: imagingType }
                    : undefined,
                  bodyPart: bodyPart
                    ? { name: bodyPart, displayName: bodyPart }
                    : undefined,
                  images: [],
                },
              },
            ],
          },
          {
            source: DocumentSource.MEDICAL_RECORDS,
            type: DocumentType.IMAGING,
            imagingId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render imaging reference.',
      );
    }

    case 'providerFacilityNote': {
      const note = asRecord(record);
      const providerName =
        compactJoin([
          note.providerFirstName as string | undefined,
          note.providerLastName as string | undefined,
        ]) ??
        (note.facilityName as string | undefined) ??
        '';
      const providerType = note.providerFacilityType as string | undefined;
      return entryToSourceContent(
        adaptProviderNotesRecords(
          {
            curated: {
              id: recordId,
              providerType: providerType
                ? { name: providerType, displayName: providerType }
                : undefined,
              alternateTypeName: note.noteTitle as string | undefined,
              providerName,
              date:
                (note.serviceStartDate as string | undefined) ??
                (note.serviceEndDate as string | undefined) ??
                '',
              comments: note.notes as string | undefined,
              createdAt:
                (note.dateUploaded as string | undefined) ??
                new Date().toISOString(),
              images: [],
            },
          },
          {
            source: DocumentSource.MEDICAL_RECORDS,
            type: DocumentType.PROVIDER_NOTES,
            providerNoteId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render provider note reference.',
      );
    }

    case 'pathologyResult': {
      const pathology = asRecord(record);
      const reason = [
        pathology.diagnosis
          ? `Diagnosis: ${pathology.diagnosis as string}`
          : undefined,
        pathology.specimenSource
          ? `Specimen: ${pathology.specimenSource as string}`
          : undefined,
      ]
        .filter(Boolean)
        .join('\n');

      return {
        kind: 'template',
        render: (ctx) =>
          createElement(PathologyTemplate, {
            details: [
              {
                id: recordId,
                sourceType: sourceType ?? 'unknown',
                sources: [
                  {
                    id: recordId,
                    contentType: 'curated',
                    procedure: {
                      id: recordId,
                      name:
                        (pathology.pathologyName as string | undefined) ??
                        'Pathology Result',
                      dateOfStudy: pathology.date as string | undefined,
                      reason: reason || undefined,
                    },
                  },
                ],
              },
            ],
            patient: ctx?.patient,
            generatedAt: new Date(),
            highlightDetailId: highlightId,
          }),
      };
    }

    case 'demographic':
      return buildDemographicSource(record);

    case 'procedureNote':
      return {
        kind: 'template',
        render: () =>
          createElement(ProcedureNoteContent, {
            procedureNote: record.notes,
          }),
      };

    case 'other':
      return {
        kind: 'template',
        render: () =>
          createElement(ReferenceUnresolvedContent, {
            reason: 'other',
            title: record.title ?? 'Other reference',
            message: record.date ? `Reference date: ${record.date}` : null,
          }),
      };

    default: {
      const _exhaustive: never = recordType;
      return {
        kind: 'template',
        render: () =>
          createElement(ReferenceUnresolvedContent, {
            reason: 'unsupportedRecordType',
            message: `Unsupported record type: ${_exhaustive}`,
          }),
      };
    }
  }
};

type ContextRecord = NonNullable<
  NonNullable<
    inferRouterOutputs<AppRouter>['patientsRouter']['getPatientFacesheetReferenceDetail']['contextRecords']
  >[number]
>;

type AdditionalContextRecordType =
  | 'familyHistory'
  | 'socialHistory'
  | 'additionalReferenceDocument';

const getAdditionalContextConfig = (
  recordType: AdditionalContextRecordType,
) => {
  switch (recordType) {
    case 'familyHistory':
      return {
        classificationType:
          UnifiedAdditionalRecordClassificationType.FAMILY_HISTORY,
        curatedField: 'curatedUnifiedAdditionalFamilyHistory' as const,
      };
    case 'socialHistory':
      return {
        classificationType:
          UnifiedAdditionalRecordClassificationType.SOCIAL_HISTORY,
        curatedField: 'curatedUnifiedAdditionalSocialHistory' as const,
      };
    case 'additionalReferenceDocument':
      return {
        classificationType:
          UnifiedAdditionalRecordClassificationType.ADDITIONAL_REFERENCE_DOCUMENTS,
        curatedField: 'curatedUnifiedAdditionalReferenceDocument' as const,
      };
  }
};

const mapAdditionalContextRecordsToSourceContent = (
  records: ContextRecord[],
  title: string,
  highlightedRecordId?: string | null,
): FacesheetSourceContent | undefined => {
  const firstCuratedRecord = records.find(
    ({ curatedRecord }) => curatedRecord,
  )?.curatedRecord;
  if (!firstCuratedRecord) return undefined;

  const recordType = firstCuratedRecord.recordType;
  if (!getAdditionalContextConfig(recordType as AdditionalContextRecordType)) {
    return undefined;
  }

  if (
    records.some(
      ({ curatedRecord }) =>
        curatedRecord && curatedRecord.recordType !== recordType,
    )
  ) {
    return undefined;
  }

  const config = getAdditionalContextConfig(
    recordType as AdditionalContextRecordType,
  );
  if (!config) return undefined;

  const entry = wrapAdditionalRecords({
    records: records.flatMap(({ recordId, curatedRecord }) =>
      curatedRecord
        ? [
            {
              recordId,
              sourceType: curatedRecord.sourceType,
              curatedValue: asRecord(curatedRecord.record),
            },
          ]
        : [],
    ),
    title,
    ...config,
    highlightRecordId: highlightedRecordId ?? undefined,
  });

  return entryToSourceContent(
    entry,
    'Unable to render the request-scoped HIE context.',
  );
};

const mapMedicalContextRecordsToSourceContent = (
  records: ContextRecord[],
  title: string,
  highlightedRecordId?: string | null,
): FacesheetSourceContent | undefined => {
  const curatedRecords = records.flatMap(({ recordId, curatedRecord }) =>
    curatedRecord ? [{ recordId, curatedRecord }] : [],
  );
  const first = curatedRecords[0];
  if (!first) return undefined;

  const source = mapFacesheetSourceType(first.curatedRecord.sourceType);
  const highlightId = highlightedRecordId ?? NO_HIGHLIGHT_ID;

  if (
    curatedRecords.some(
      ({ curatedRecord }) =>
        curatedRecord.recordType !== first.curatedRecord.recordType,
    )
  ) {
    return undefined;
  }

  switch (first.curatedRecord.recordType) {
    case 'allergy':
      return entryToSourceContent(
        adaptAllergiesData(
          {
            allergies: curatedRecords.map(
              ({ curatedRecord }) => curatedRecord.record,
            ) as never,
          },
          {
            source: DocumentSource.MEDICAL_DATA,
            type: DocumentType.ALLERGIES,
            allergyId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render the request-scoped HIE context.',
      );
    case 'problem':
      return entryToSourceContent(
        adaptProblemsData(
          {
            problems: curatedRecords.map(
              ({ curatedRecord }) => curatedRecord.record,
            ) as never,
          },
          {
            source: DocumentSource.MEDICAL_DATA,
            type: DocumentType.PROBLEMS,
            problemId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render the request-scoped HIE context.',
      );
    case 'procedure':
      return entryToSourceContent(
        adaptProceduresData(
          {
            procedures: curatedRecords.map(
              ({ curatedRecord }) => curatedRecord.record,
            ) as never,
          },
          {
            source: DocumentSource.MEDICAL_DATA,
            type: DocumentType.PROCEDURES,
            procedureId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render the request-scoped HIE context.',
      );
    case 'medication': {
      const medications = curatedRecords.map(({ recordId, curatedRecord }) => {
        const medication = asRecord(curatedRecord.record);
        return {
          ...medication,
          id: recordId,
          medicationCodes: unwrapItems(
            medication.medicationCodes as StringItems,
          ),
          surescriptsMedicationDetails:
            medication.surescriptsMedicationDetails ??
            medication.surescriptsDetails,
        };
      });
      return entryToSourceContent(
        adaptMedicationsData(
          { medications: medications as never },
          {
            source: DocumentSource.MEDICAL_DATA,
            type: DocumentType.MEDICATIONS,
            medicationId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render the request-scoped HIE context.',
      );
    }
    case 'vital': {
      const vitals = curatedRecords.map(({ recordId, curatedRecord }) => {
        const vital = asRecord(curatedRecord.record);
        return {
          ...vital,
          id: recordId,
          name:
            (vital.observationName as string | undefined) ??
            (vital.name as string | undefined),
          value:
            (vital.observationValue as string | undefined) ??
            (vital.value as string | undefined),
          unit:
            (vital.observationUnit as string | undefined) ??
            (vital.unit as string | undefined),
          observedAt:
            (vital.observationDatetime as string | undefined) ??
            (vital.observedAt as string | undefined),
          text:
            (vital.observationText as string | undefined) ??
            (vital.text as string | undefined),
          category:
            (vital.observationCategory as string | undefined) ??
            (vital.category as string | undefined),
        };
      });
      return entryToSourceContent(
        adaptVitalsData(
          {
            groupingObservationId:
              (asRecord(first.curatedRecord.record).groupingObservationId as
                | string
                | undefined) ?? first.recordId,
            vitals: vitals as never,
          },
          {
            source: DocumentSource.MEDICAL_DATA,
            type: DocumentType.VITALS,
            vitalId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render the request-scoped HIE context.',
      );
    }
    case 'labPanel':
      return entryToSourceContent(
        adaptLabResultsData(
          {
            labPanels: curatedRecords.map(({ recordId, curatedRecord }) => {
              const panel = asRecord(curatedRecord.record);
              return {
                ...panel,
                id: recordId,
                rows: (panel.rows as unknown[] | undefined) ?? [],
                interpreters:
                  (panel.interpreters as unknown[] | undefined) ?? [],
                performers: (panel.performers as unknown[] | undefined) ?? [],
              };
            }) as never,
          },
          {
            source: DocumentSource.MEDICAL_DATA,
            type: DocumentType.LAB_RESULTS,
            labPanelId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render the request-scoped HIE context.',
      );
    case 'labResult':
      return entryToSourceContent(
        adaptLabResultsData(
          {
            labPanels: curatedRecords.map(({ recordId, curatedRecord }) => {
              const lab = asRecord(curatedRecord.record);
              return {
                id: recordId,
                labPanel:
                  (lab.labTest as string | undefined) ??
                  (lab.name as string | undefined) ??
                  'Lab Result',
                dateOfStudy:
                  (lab.dateOfStudy as string | undefined) ??
                  (lab.recordedDatetime as string | undefined),
                rows: [
                  {
                    labTest:
                      (lab.labTest as string | undefined) ??
                      (lab.name as string | undefined),
                    value: lab.value as string | undefined,
                    units:
                      (lab.units as string | undefined) ??
                      (lab.unit as string | undefined),
                    dateOfStudy:
                      (lab.dateOfStudy as string | undefined) ??
                      (lab.recordedDatetime as string | undefined),
                    interpretation: lab.interpretation as string | undefined,
                  },
                ],
                interpreters: [],
                performers: [],
              };
            }) as never,
          },
          {
            source: DocumentSource.MEDICAL_DATA,
            type: DocumentType.LAB_RESULTS,
            labPanelId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render the request-scoped HIE context.',
      );
    case 'vaccination':
      return entryToSourceContent(
        adaptVaccinationsData(
          {
            vaccinations: curatedRecords.map(({ recordId, curatedRecord }) => ({
              ...(curatedRecord.record as object),
              id: recordId,
              name:
                typeof (curatedRecord.record as { name?: unknown }).name ===
                'string'
                  ? (curatedRecord.record as { name: string }).name
                  : 'Vaccination',
            })) as never,
          },
          {
            source: DocumentSource.MEDICAL_DATA,
            type: DocumentType.VACCINATIONS,
            vaccinationId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render the request-scoped HIE context.',
      );
    case 'provider':
      return entryToSourceContent(
        adaptPatientProviders(
          {
            providers: curatedRecords.map(({ recordId, curatedRecord }) => ({
              ...(curatedRecord.record as object),
              id: recordId,
              telephones: unwrapItems(
                (curatedRecord.record as { telephones?: StringItems })
                  .telephones,
              ),
            })) as never,
          },
          {
            source: DocumentSource.PROVIDERS,
            type: DocumentType.PATIENT_PROVIDERS,
            providerId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render the request-scoped HIE context.',
      );
    case 'encounter': {
      const encounters = curatedRecords.map(({ recordId, curatedRecord }) => {
        const encounter = asRecord(curatedRecord.record);
        const documentReferences = (
          (encounter.documentReferences as Array<Record<string, unknown>>) ?? []
        ).map((docRef) => {
          const metadata = docRef.documentMetadata as
            | { signedUrl?: string }
            | undefined;
          return {
            referenceType: docRef.referenceType as string | undefined,
            contentType: docRef.contentType as string | undefined,
            signedUrl:
              (docRef.signedUrl as string | undefined) ?? metadata?.signedUrl,
          };
        });
        return {
          id: recordId,
          name:
            (encounter.typeName as string | undefined) ??
            (encounter.encounterText as string | undefined) ??
            'Encounter',
          dateOfStudy:
            (encounter.startTime as string | undefined) ??
            (encounter.endTime as string | undefined) ??
            '',
          sourceType: curatedRecord.sourceType ?? 'unspecified',
          curated: {
            id: recordId,
            encounterText: encounter.encounterText as string | undefined,
            typeName: encounter.typeName as string | undefined,
            startTime: encounter.startTime as string | undefined,
            endTime: encounter.endTime as string | undefined,
            hospitalizationDischargeDisposition:
              encounter.hospitalizationDischargeDisposition as
                | string
                | undefined,
            documentReferences,
            locations: (encounter.locations as never) ?? [],
            conditions: (encounter.conditions as never) ?? [],
            practitioners: (encounter.practitioners as never) ?? [],
          },
        };
      });
      return entryToSourceContent(
        adaptEncountersRecords(
          { encounters: encounters as never },
          {
            source: DocumentSource.MEDICAL_RECORDS,
            type: DocumentType.ENCOUNTERS,
            encounterId: highlightId,
            highlightEncounterId: highlightId,
            sourceType: source,
            name: title,
          },
        ),
        'Unable to render the request-scoped HIE context.',
      );
    }
    default:
      return undefined;
  }
};

export const mapCuratedRecordsToSourceContent = (
  records: ContextRecord[],
  title: string,
  highlightedRecordId?: string | null,
): FacesheetSourceContent => {
  // Use the same adapters as the Medical Data section whenever the curated
  // record type is supported there. Additional-record adapters are reserved
  // for HIE tables (family/social history, reference documents) that have no
  // equivalent Medical Data adapter.
  const medicalAggregated = mapMedicalContextRecordsToSourceContent(
    records,
    title,
    highlightedRecordId,
  );
  if (medicalAggregated) return medicalAggregated;

  const aggregated = mapAdditionalContextRecordsToSourceContent(
    records,
    title,
    highlightedRecordId,
  );
  if (aggregated) return aggregated;

  return {
    kind: 'template',
    anchorId: highlightedRecordId
      ? `hie-record-${highlightedRecordId}`
      : undefined,
    render: (ctx) =>
      createElement(
        'div',
        { className: 'space-y-8', role: 'document', 'aria-label': title },
        ...records.map(({ recordId, curatedRecord }) => {
          const content = curatedRecord
            ? mapCuratedRecordToSourceContent(curatedRecord, title, {
                highlightRecordId: highlightedRecordId ?? undefined,
              })
            : undefined;
          const inner = content
            ? content.kind === 'template'
              ? content.render(ctx)
              : createElement(
                  'a',
                  {
                    href: content.signedUrl,
                    target: '_blank',
                    rel: 'noreferrer',
                    className: 'text-primary underline',
                  },
                  'Open source document',
                )
            : createElement(ReferenceUnresolvedContent, {
                reason: 'missingContent',
                message: 'This reference could not be rendered.',
              });

          return createElement(
            'div',
            {
              key: recordId,
              id: `hie-record-${recordId}`,
              className:
                recordId === highlightedRecordId
                  ? 'rounded-lg ring-2 ring-primary ring-offset-2'
                  : undefined,
            },
            inner,
          );
        }),
      ),
  };
};
